import {
  Box,
  CircularProgress,
  Container,
  Link,
  Typography,
  makeStyles
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React, { useEffect, useRef, useState } from 'react'
import { RiArrowLeftLine } from 'react-icons/ri'
import { useHistory, useParams } from 'react-router-dom'
import {
  FormularioRegularizacaoResponse,
  MinhaPendenciaItem,
  obterFormularioRegularizacao,
  obterMinhasPendencias,
  regularizarRecebedor
} from 'src/api/recebedor-pendencia-bacen'
import Button from 'src/components/ui/Button'
import { SynRoutes } from 'src/core/consts/synRoutes'
import {
  showNotification,
  showNotificationErrorAPI
} from 'src/core/helpers/notification'
import useAuth from 'src/hooks/useAuth'
import FormularioRegularizacao from './formularioRegularizacao'
import DrawerCadastroPagarme from './drawerCadastroPagarme'

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    maxWidth: '100%',
    width: '100%'
  },
  fila: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    alignItems: 'center'
  },
  voltar: {
    display: 'inline-flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0
  }
}))

type Props = {
  pendenciaIdInicial?: string
  modoPainel?: boolean
  onVoltarPainel?: () => void
}

function BotaoVoltarLista({
  visivel,
  className,
  onClick
}: {
  visivel: boolean
  className: string
  onClick: () => void
}) {
  if (!visivel) return null

  return (
    <Link
      component="button"
      type="button"
      color="primary"
      className={className}
      onClick={onClick}
      aria-label="Voltar à lista do painel"
    >
      <RiArrowLeftLine />
      <Typography
        style={{ marginLeft: 10 }}
        variant="subtitle1"
        component="span"
      >
        Voltar
      </Typography>
    </Link>
  )
}

export default function RegularizacaoBacenPage({
  pendenciaIdInicial,
  modoPainel = false,
  onVoltarPainel
}: Props) {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{ pendenciaId?: string }>()
  const { recarregarPendenciaBacen } = useAuth()
  const idInicial = pendenciaIdInicial || params.pendenciaId || null

  const [carregandoLista, setCarregandoLista] = useState(!modoPainel)
  const [carregandoForm, setCarregandoForm] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [itens, setItens] = useState<MinhaPendenciaItem[]>([])
  const [pendenciaId, setPendenciaId] = useState<string | null>(idInicial)
  const [formulario, setFormulario] = useState<FormularioRegularizacaoResponse | null>(
    null
  )
  const [erroServidor, setErroServidor] = useState<string | null>(null)
  const [erroRequerAcaoSyn, setErroRequerAcaoSyn] = useState(false)
  const [drawerCadastroAberto, setDrawerCadastroAberto] = useState(false)
  const pedidoFormRef = useRef(0)

  useEffect(() => {
    if (modoPainel) {
      setCarregandoLista(false)
      if (idInicial) setPendenciaId(idInicial)
      return
    }

    let ativo = true

    async function carregarPendencias() {
      setCarregandoLista(true)
      try {
        const { data } = await obterMinhasPendencias()
        if (!ativo) return

        const lista = (data.itens || []).filter(i => !i.requerAcaoSyn)
        setItens(lista)

        if (lista.length === 0) {
          setPendenciaId(null)
          setFormulario(null)
          return
        }

        setPendenciaId(atual => atual || lista[0].pendenciaId)
      } catch (erro) {
        if (ativo) showNotificationErrorAPI(erro)
      } finally {
        if (ativo) setCarregandoLista(false)
      }
    }

    carregarPendencias()
    return () => {
      ativo = false
    }
  }, [modoPainel, idInicial])

  useEffect(() => {
    if (!pendenciaId) return

    let ativo = true
    const pedido = ++pedidoFormRef.current

    async function carregarFormulario() {
      setCarregandoForm(true)
      setErroServidor(null)
      setErroRequerAcaoSyn(false)
      try {
        const { data } = await obterFormularioRegularizacao(pendenciaId)
        if (!ativo || pedido !== pedidoFormRef.current) return
        setFormulario(data)
      } catch (erro) {
        if (!ativo || pedido !== pedidoFormRef.current) return
        const mensagem =
          erro?.response?.data?.erros?.join?.(', ') ||
          'Não foi possível carregar o formulário.'
        if (/equipe Syn/i.test(mensagem)) {
          setErroRequerAcaoSyn(true)
          setErroServidor(mensagem)
          setFormulario(null)
        } else {
          showNotificationErrorAPI(erro)
          setFormulario(null)
        }
      } finally {
        if (ativo && pedido === pedidoFormRef.current) {
          setCarregandoForm(false)
        }
      }
    }

    carregarFormulario()
    return () => {
      ativo = false
    }
  }, [pendenciaId])

  async function enviarValores(valores: Record<string, string>) {
    if (!pendenciaId) return

    setEnviando(true)
    setErroServidor(null)

    try {
      const { data } = await regularizarRecebedor(pendenciaId, {
        pendenciaId,
        valores
      })

      showNotification('Sucesso', 'Dados enviados com sucesso.', 'success', true)

      if (modoPainel) {
        await recarregarPendenciaBacen()
        if (onVoltarPainel) onVoltarPainel()
        else history.push(SynRoutes.painelRegularizacaoBacen)
        return
      }

      if (data.restantes > 0 && data.proximaPendenciaId) {
        setItens(atuais => atuais.filter(i => i.pendenciaId !== pendenciaId))
        setPendenciaId(data.proximaPendenciaId)
        return
      }

      const { data: atualizadas } = await obterMinhasPendencias()
      const restantes = (atualizadas.itens || []).filter(i => !i.requerAcaoSyn)
      setItens(restantes)
      await recarregarPendenciaBacen()

      if (restantes.length === 0) {
        showNotification(
          'Pendências concluídas',
          'Todos os recebedores sob sua responsabilidade foram regularizados.',
          'success'
        )
        history.push(SynRoutes.home)
        return
      }

      setPendenciaId(restantes[0].pendenciaId)
    } catch (erro) {
      const mensagem =
        erro?.response?.data?.erros?.join?.(', ') ||
        'Não foi possível concluir o envio. Revise os dados e tente novamente.'
      setErroServidor(mensagem)
    } finally {
      setEnviando(false)
    }
  }

  function voltarLista() {
    if (onVoltarPainel) onVoltarPainel()
    else history.push(SynRoutes.painelRegularizacaoBacen)
  }

  if (carregandoLista) {
    return (
      <Container maxWidth={false} className={classes.root}>
        <CircularProgress aria-label="Carregando pendências" />
      </Container>
    )
  }

  if (erroRequerAcaoSyn) {
    return (
      <Container maxWidth={false} className={classes.root}>
        <BotaoVoltarLista
          visivel={modoPainel}
          className={classes.voltar}
          onClick={voltarLista}
        />
        <Typography variant="h5" component="h1" gutterBottom>
          Regularização cadastral
        </Typography>
        <Alert severity="info">{erroServidor}</Alert>
        {modoPainel && pendenciaId && (
          <Box mt={2}>
            <Button
              type="button"
              variant="contained"
              color="primary"
              name="ver-informacao-completa"
              onClick={() => setDrawerCadastroAberto(true)}
              aria-haspopup="dialog"
            >
              Ver informação completa
            </Button>
          </Box>
        )}
        <DrawerCadastroPagarme
          pendenciaId={pendenciaId}
          open={drawerCadastroAberto}
          onClose={() => setDrawerCadastroAberto(false)}
        />
      </Container>
    )
  }

  if (!pendenciaId || (!modoPainel && itens.length === 0)) {
    return (
      <Container maxWidth={false} className={classes.root}>
        <Typography variant="h5" component="h1" gutterBottom>
          Regularização cadastral
        </Typography>
        <Alert severity="success">
          Não há pendências de dados bancários sob sua responsabilidade.
        </Alert>
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push(SynRoutes.home)}
          >
            Voltar ao início
          </Button>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth={false} className={classes.root}>
      <BotaoVoltarLista
        visivel={modoPainel}
        className={classes.voltar}
        onClick={voltarLista}
      />
      <Typography variant="h5" component="h1" gutterBottom>
        Regularização cadastral
      </Typography>

      {!modoPainel && (
        <Box className={classes.fila} aria-label="Fila de recebedores pendentes">
          <Typography variant="body1">
            {formulario
              ? `${formulario.posicao} de ${formulario.totalPendentes}`
              : `${itens.findIndex(i => i.pendenciaId === pendenciaId) + 1} de ${
                  itens.length
                }`}
          </Typography>
          {itens.map(item => (
            <Button
              key={item.pendenciaId}
              size="small"
              variant={
                item.pendenciaId === pendenciaId ? 'contained' : 'outlined'
              }
              color="primary"
              disabled={enviando || carregandoForm}
              onClick={() => setPendenciaId(item.pendenciaId)}
            >
              {item.nome}
            </Button>
          ))}
        </Box>
      )}

      {carregandoForm || !formulario ? (
        <CircularProgress aria-label="Carregando formulário" />
      ) : (
        <FormularioRegularizacao
          key={formulario.pendenciaId}
          formulario={formulario}
          enviando={enviando}
          erroServidor={erroServidor}
          onSubmit={enviarValores}
          onVerInformacaoCompleta={() => setDrawerCadastroAberto(true)}
        />
      )}

      <DrawerCadastroPagarme
        pendenciaId={pendenciaId}
        open={drawerCadastroAberto}
        onClose={() => setDrawerCadastroAberto(false)}
      />
    </Container>
  )
}
